import { Filter, PlusSquare, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";

import CadastroAgenciaService from "@/services/cadastroAgencia";
import GetAllClientes from "@/services/getClientes";
import { ModalCriacaoCampanha } from "../../ModalCriacaoCampanha/ModalCriacaoCampanha";
import Select from "react-select";
import { ViewCampanha } from "@/pages/UserGoOn/Campanhas/ViewCampanha";
import debounce from "lodash.debounce";
import { mesesPipeline } from "@/components/utils/Utils";
import { serviceAreaVendas } from "@/services/serviceAreaVendas";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLayout } from "@/contexts/UseLayout";
import TemPermissao from "@/components/utils/NiveisDeAcesso";

interface Props {
  idMenu: any;
}

const getCampaignStatusClass = (statusId: any) => {
  if (statusId == 1) return "text-verde-300";
  if (statusId == 3) return "text-cinza-300";
  return "text-azul-400";
};

export function Campanhas({ idMenu }: Props) {
  const [openAddCampanha, setOpenAddCampanha] = useState<boolean>(false);
  const { t } = useTranslation();
  const context = useLayout();

  const [openViewCampanha, setOpenViewCampanha] = useState<boolean>(false);
  const [dataCapanha, setDataCampanha] = useState<any>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [payload, setPayload] = useState<any>({});
  const [openFilters, setOpenFilters] = useState(false);

  const { isPending, data, refetch } = useQuery({
    queryKey: ["campanhas", currentPage, payload],
    queryFn: async () =>
      await serviceAreaVendas?.getCampanhas(
        currentPage,
        3,
        "",
        payload?.clienteId?.value,
        payload?.agenciaId?.value,
        payload?.statusId?.value,
        payload?.ano?.value,
        payload?.mes?.value,
      ),
    enabled: idMenu === "campanhas",
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  const [clientes, setClientes] = useState<any>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const [agencys, setAgencys] = useState<any>([]);
  const [loadingAgency, setLoadingAgency] = useState(false);

  const loadClientes = useCallback(
    debounce(async (inputValue) => {
      if (inputValue) {
        setLoadingClientes(true);
        const res = await GetAllClientes.listClientesPagination(
          1,
          10,
          inputValue,
        );
        if (res.status === 200) {
          setClientes(res.data.data);
        }
        setLoadingClientes(false);
      } else {
        setClientes([]);
      }
    }, 300),
    [],
  );

  const handleInputChangeClientes = (inputValue: any) => {
    loadClientes(inputValue);
    return inputValue;
  };

  const loadAgencys = useCallback(
    debounce(async (inputValue) => {
      if (inputValue) {
        setLoadingAgency(true);
        const res = await CadastroAgenciaService.listarAgencys(
          1,
          10,
          inputValue,
        );
        if (res.status === 200) {
          setAgencys(res.data.data);
        }
        setLoadingAgency(false);
      } else {
        setAgencys([]);
      }
    }, 300),
    [],
  );

  const handleInputChangeAgency = (inputValue: any) => {
    loadAgencys(inputValue);
    return inputValue;
  };

  const statusOptions = [
    {
      value: 1,
      label: t("campaigns.status.active"),
    },
    {
      value: 2,
      label: t("campaigns.status.competition"),
    },
    {
      value: 3,
      label: t("campaigns.status.finished"),
    },
  ];

  return (
    <>
      <div>
        <div>
          <div className="flex items-center gap-x-6">
            <h2 className="text-xl font-medium">{t("menu.campaigns")}</h2>
            {TemPermissao(context.userLogged, "campanhas", "criar") && (
              <button type="button" onClick={() => setOpenAddCampanha(true)}>
                <PlusSquare size={20} color="#153DCE " />{" "}
              </button>
            )}
          </div>
          <p className="text-xs -mt-1 text-cinza-300">
            {data?.data?.qtdRegistros || 0} {t("campaigns.campaigns_lowercase")}
          </p>
        </div>
        <div>
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setOpenFilters(!openFilters)}
              className="flex items-center gap-1 text-sm bg-azul-400 text-white px-2 rounded-md my-2"
            >
              <Filter size={14} />
              {t("common:filters")}
            </button>
            {openFilters && (
              <button
                type="button"
                onClick={() => setPayload({})}
                className="flex items-center gap-1 text-sm bg-red-500 text-white px-2 rounded-md my-2"
              >
                <Trash2 size={14} />
                {t("common:clear")}
              </button>
            )}
          </div>
          {openFilters && (
            <div className="space-y-2">
              <Select
                placeholder={t("campaigns.fields.client")}
                className="col-span-2"
                onInputChange={handleInputChangeClientes}
                isLoading={loadingClientes}
                onChange={(value: any) => {
                  setPayload({ ...payload, clienteId: value });
                }}
                value={payload.clienteId}
                options={[
                  ...clientes.map((e: any) => ({
                    value: e.id,
                    label: e.name_fantasy,
                  })),
                ]}
                noOptionsMessage={() => t("common:type_to_search")}
              />
              <Select
                placeholder={t("campaigns.fields.agency")}
                onInputChange={handleInputChangeAgency}
                isLoading={loadingAgency}
                onChange={(value: any) => {
                  setPayload({ ...payload, agenciaId: value });
                }}
                value={payload.agenciaId}
                options={[
                  ...agencys.map((e: any) => ({
                    value: e.id,
                    label: e.name_fantasy,
                    data: e,
                  })),
                ]}
                noOptionsMessage={() => t("common:type_to_search")}
              />
              <Select
                placeholder={t("campaigns.fields.status")}
                onChange={(value: any) => {
                  setPayload({ ...payload, statusId: value });
                }}
                value={payload.statusId}
                options={statusOptions}
              />
              <Select
                placeholder={t("campaigns.fields.year")}
                onChange={(value: any) => {
                  setPayload({ ...payload, ano: value });
                }}
                value={payload.ano}
                options={[
                  {
                    value: 2024,
                    label: "2024",
                  },
                  {
                    value: 2025,
                    label: "2025",
                  },
                  {
                    value: 2026,
                    label: "2026",
                  },
                ]}
              />
              <Select
                placeholder={t("campaigns.fields.month")}
                onChange={(value: any) => {
                  setPayload({ ...payload, mes: value });
                }}
                value={payload.mes}
                options={[
                  ...mesesPipeline(t).map((e: any) => ({
                    value: e.value,
                    label: e.label,
                  })),
                ]}
                noOptionsMessage={() => t("common:type_to_search")}
              />
            </div>
          )}
        </div>
        {openAddCampanha && (
          <ModalCriacaoCampanha
            setIsOpen={setOpenAddCampanha}
            isOpen={openAddCampanha}
          />
        )}
        <div className="mt-6">
          <ul className="space-y-4">
            {!!isPending && (
              <>
                {Array.from({ length: 5 }).map((_, index) => (
                  <li
                    key={index}
                    className="p-2 bg-gray-100 rounded-lg shadow-md flex justify-between items-center animate-pulse"
                  >
                    <div className="w-full flex items-center gap-x-4">
                      <div className="flex gap-2 items-center w-2/12">
                        <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                      </div>
                      <div className="w-10/12 space-y-2">
                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                      </div>
                    </div>
                  </li>
                ))}
              </>
            )}
            {!isPending &&
              data?.data?.data?.map((item: any) => (
                <div>
                  <button
                    key={item?.id}
                    onClick={() => {
                      (setOpenViewCampanha(true), setDataCampanha(item));
                    }}
                    className="px-4 py-3 bg-gray-100 rounded-lg shadow-md flex justify-between items-center relative w-full"
                  >
                    <div className="w-full">
                      <div className="flex flex-col gap-x-2 items-start">
                        {/* <img
                                                src={item?.filePath ?? ImageGeneric}
                                                alt={item?.nameCliente}
                                                className="w-8 h-8 object-cover rounded-full"
                                            /> */}
                        <p className="font-semibold text-sm">
                          {t("campaigns.fields.client")}:
                        </p>
                        <div className="flex items-center gap-1 my-1">
                          <img
                            src={item?.filePath}
                            alt=""
                            className="w-5 h-5 rounded-full"
                          />
                          <p className="font-semibold text-start text-xs text-gray-500">
                            {item?.nameCliente}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col justify-start items-start gap-x-2">
                        <p className="font-semibold text-sm">
                          {t("campaigns.campaign")}:
                        </p>
                        <p className="text-xs text-gray-500">{item?.nome}</p>
                      </div>
                      <div className="flex flex-col justify-start items-start gap-x-2">
                        <p className="font-semibold text-sm">
                          {t("campaigns.fields.agency")}:
                        </p>
                        <p className="text-xs text-gray-500">
                          {item?.nomeAgencia}
                        </p>
                      </div>
                      <div className="flex flex-col justify-start items-start gap-x-2">
                        <p className="font-semibold text-sm">
                          {t("campaigns.fields.status")}:
                        </p>
                        <p
                          className={`text-xs ${getCampaignStatusClass(
                            item?.statusCampanhaId,
                          )}`}
                        >
                          {
                            statusOptions.find(
                              (option) =>
                                option.value == item?.statusCampanhaId,
                            )?.label
                          }
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
          </ul>
          {data?.data?.qtdRegistros == 0 && (
            <p className="text-gray-500 text-center">
              {t("campaigns.no_campaigns_found")}
            </p>
          )}
          {!isPending && data?.data?.qtdRegistros >= 5 && (
            <div className="flex items-center justify-center mt-6 space-x-4 text-sm">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-azul-400 text-white rounded-lg disabled:bg-gray-400"
              >
                {t("common:pagination.previous")}
              </button>
              <span className="text-sm">
                {t("common:pagination.page")} {currentPage}{" "}
                {t("common:pagination.of")}{" "}
                {Math.ceil(data?.data?.qtdRegistros / 3)}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={
                  currentPage === Math.ceil(data?.data?.qtdRegistros / 3)
                }
                className="px-3 py-2 bg-azul-400 text-white rounded-lg disabled:bg-gray-400"
              >
                {t("common:pagination.next")}
              </button>
            </div>
          )}
        </div>
      </div>
      {openViewCampanha && (
        <ViewCampanha
          isOpen={openViewCampanha}
          close={() => setOpenViewCampanha(false)}
          refetch={refetch}
          campanha={dataCapanha}
        />
      )}
    </>
  );
}
